import React, { useContext, useEffect, useState } from "react";
import BacktestingWrapper from "features/bots/BotForm/Backtesting/BacktestingPage/BacktestingWrapper";
import BacktestingHistoryTable from "features/bots/BotForm/Backtesting/BacktestingPage/BacktestingHistoryTable";
import {
  BASE_BOTS_PATH,
  DEFAULT_PADDING,
  SMALL_MOBILE,
  SMALL_MOBILE_PADDING,
} from "common/constants";
import styled, { useTheme } from "styled-components";
import {
  NEW_LOOK_CARDS_VIEW_MAX_WIDTH,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook/layout";
import FiltersColumn from "common/Filters/FiltersColumn/FiltersColumn";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import {
  BacktestingRunType,
  BacktestingStatus,
  BotType,
  LanguageNameSpaces,
  QueryBotType,
} from "@shared/types";
import ConfirmDeleteModal from "features/bots/BotForm/Backtesting/ConfirmDeleteModal";
import useMedia from "utils/hooks/useMedia";
import EmptyView from "common/Empty/EmptyView";
import BacktestingHistoryCards from "features/bots/BotForm/Backtesting/BacktestingPage/BacktestingHistoryCards";
import useBacktestingPage from "@shared/features/bots/BotForm/Backtesting/useBacktestingPage";
import { HEADER_HEIGHT } from "common/Home/General/constants.tsx";
import { useRouteMatch } from "react-router-dom";
import { QUERY_BOT_TYPE_TO_BOT_TYPE } from "@shared/common/constants";
import { useBottomNavigator } from "utils/hooks/misc";
import { EmptyStatesType } from "common/Icons/types.ts";
import { PageTabsContext } from "common/Tabs/PageTabs.context.ts";
import { MobileBotsTab } from "@shared/features/bots/types.ts";
import { PageWrapperFiltersContext } from "common/Home/PageWrapper/PageWrapperFilters.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import useSearchInput from "utils/hooks/useSearchInput";
import { useNavigateToBacktestDetails } from "features/bots/common/hooks";
import { getLiveBacktestingStatus } from "@shared/features/bots/BotForm/Backtesting/utils";
import { FAILED_BACKTESTING_EXTRA_DETAILS_MESSAGE } from "@shared/features/bots/BotForm/common/constants";
import { openMessage } from "utils/misc";
import NewLookBacktestingList from "features/bots/BotForm/Backtesting/NewLook/NewLookBacktestingList";

const BacktestingPage = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { filters } = useContext(PageWrapperFiltersContext);
  const { distanceTop, useMobileSearch } = useContext(PageWrapperContext);
  const { setTabCount } = useContext(PageTabsContext) || {};
  const useFiltersChips = useMedia(["(max-width: 1750px)"], [true], false);
  const padding = useMedia(
    [`(max-width: ${SMALL_MOBILE})`],
    [SMALL_MOBILE_PADDING],
    DEFAULT_PADDING
  );
  const contentDistanceTop = (distanceTop || 0) + HEADER_HEIGHT + padding;
  const contentHeight = `calc(100vh - ${contentDistanceTop}px - ${padding}px)`;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isNewLook } = useTheme();
  const isNarrowScreen = useMedia(
    [`(max-width: ${NEW_LOOK_CARDS_VIEW_MAX_WIDTH})`],
    [true],
    false
  );

  const match = useRouteMatch(BASE_BOTS_PATH);
  const { botType: queryBotType } = (match?.params || {}) as {
    botType?: QueryBotType;
  };
  const botType =
    (queryBotType ? QUERY_BOT_TYPE_TO_BOT_TYPE[queryBotType] : undefined) ??
    BotType.SIGNAL;

  const [isCardsView, setIsCardsView] = useState(
    isBottomNavigator || (isNewLook && isNarrowScreen)
  );
  const [searchValue, setSearchValue] = useState("");
  const [SearchInput, searchInputValue] = useSearchInput({
    placeholder: tCommon("labels.search_placeholder"),
    style: { width: "200px", borderRadius: 8 },
    lowerCaseValue: true,
  });
  const { navigateToBacktestDetails } = useNavigateToBacktestDetails();

  const {
    isLoading,
    isFetching,
    backtestingRuns,
    filtersData,
    backtestingToDelete,
    setBacktestingToDelete,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    onRefresh,
    currentData,
  } = useBacktestingPage({
    searchValue,
    botType,
  });

  useEffect(() => {
    if (!isLoading) {
      setTabCount?.(
        MobileBotsTab.BACKTESTING,
        currentData ? backtestingRuns.length : undefined
      );
    }
  }, [isLoading, backtestingRuns]);

  usePageWrapper({
    filters: filtersData,
    showSearch: true,
    hideMobileFilters: true,
  });

  useEffect(() => {
    if (isBottomNavigator) {
      setIsCardsView(true);
    }
  }, [isBottomNavigator]);

  const emptyState = (
    <EmptyView
      iconKey={EmptyStatesType.BacktestingHistory}
      description={t("backtesting.empty_states.no_backtesting_history")}
      iconWidth={110}
    />
  );

  const commonProps = {
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
  };

  const openBacktestDetails = (backtestData: BacktestingRunType) => {
    if (getLiveBacktestingStatus(backtestData) === BacktestingStatus.FAILED) {
      openMessage(t(FAILED_BACKTESTING_EXTRA_DETAILS_MESSAGE), "warning");
    } else {
      navigateToBacktestDetails(backtestData.bot_id, backtestData.id);
    }
  };

  const getBacktestingHistory = () => {
    if (isNewLook && !isBottomNavigator) {
      return (
        <NewLookBacktestingList
          botType={botType}
          data={backtestingRuns}
          isCardsView={isCardsView}
          isLoading={isLoading || (isFetching && !currentData)}
          isFiltered={!!currentData?.data?.length}
          search={useMobileSearch ? undefined : SearchInput}
          openDeleteModal={setBacktestingToDelete}
          onRowClick={openBacktestDetails}
          {...commonProps}
        />
      );
    }
    const props = {
      botType,
      data: backtestingRuns,
      emptyState,
      openDeleteModal: setBacktestingToDelete,
      isFetching,
    };
    if (isCardsView) {
      return (
        <BacktestingHistoryCards
          {...props}
          onRefresh={onRefresh}
          isLoading={isLoading}
        />
      );
    } else {
      return <BacktestingHistoryTable {...props} {...commonProps} />;
    }
  };

  return (
    <Wrapper>
      <ContentWrapper
        style={{
          width: useFiltersChips
            ? "100%"
            : `calc(100% - ${FILTERS_COLUMN_WIDTH + padding}px)`,
        }}
      >
        <BacktestingWrapper
          SearchInput={SearchInput}
          searchInputValue={searchInputValue}
          isCardsView={isCardsView}
          setIsCardsView={setIsCardsView}
          setSearchValue={setSearchValue}
          useFiltersChips={useFiltersChips}
          padding={padding}
          {...commonProps}
        />
        <div
          style={
            isBottomNavigator
              ? { height: "100%" }
              : isNewLook
              ? { padding: `0 ${NEW_LOOK_PAGE_GUTTER_VAR} 24px` }
              : { padding: `0 ${padding}px ${padding}px` }
          }
        >
          {getBacktestingHistory()}
        </div>
      </ContentWrapper>
      {!isLoading && filters && !useFiltersChips && (
        <FiltersColumn
          selectLoadingStates={filters.loadingStatesSelector}
          filtersActions={filters.actions}
          filtersSelectors={filters.selectors}
          filterTypeToTitle={filters.filterTypeToTitle}
          height={contentHeight}
          width={FILTERS_COLUMN_WIDTH}
          expanded={true}
          style={{ margin: `${padding}px ${padding}px 0 0` }}
        />
      )}
      <ConfirmDeleteModal
        closeModal={() => setBacktestingToDelete(undefined)}
        backtestingRun={backtestingToDelete}
        isSeeAllScreen={true}
        botType={botType}
      />
    </Wrapper>
  );
};

export default BacktestingPage;

const FILTERS_COLUMN_WIDTH = 300;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: inherit;
  height: 100%;
`;
const ContentWrapper = styled.div`
  position: relative;
  height: 100%;
`;
